import { documentShow } from '@features/documents/model/actions.js';
import { useMessages } from '@features/l10n/l10nInjector.js';
import { FmDropdownMenu } from '@shared/components/FmDropdownMenu.js';
import { LongPressTooltip } from '@shared/components/LongPressTooltip.js';
import { OnlineOnlyItem } from '@shared/components/OnlineOnlyItem.js';
import { Toolbar } from '@shared/components/Toolbar.js';
import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import { modalMenuItemProps } from '@shared/hooks/useMenuHandler.js';
import { integratedLayerDefs } from '@shared/mapDefinitions.js';
import { type ReactElement, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  FaFileContract,
  FaInfoCircle,
  FaList,
  FaLock,
  FaMoneyBillWave,
  FaRegCopyright,
} from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { setActiveModal } from '../store/actions.js';
import { useAttributionInfo } from './useAttributionInfo.js';

export function CopyrightButton(): ReactElement {
  const m = useMessages();

  const dispatch = useDispatch();

  const skCs = useAppSelector((state) =>
    ['sk', 'cs'].includes(state.l10n.language),
  );

  const customLayers = useAppSelector((state) => state.map.customLayers);

  const layers = useAppSelector((state) => state.map.layers);

  const legendLayers = useMemo(
    () =>
      new Set([
        ...(skCs ? ['A', 'T', 'C', 'K'] : []),
        'X',
        'O',
        ...[...integratedLayerDefs, ...customLayers]
          .filter((def) => def.technology === 'wms')
          .map((def) => def.type),
      ]),

    [customLayers, skCs],
  );

  const showLegendButton = layers.some((type) => legendLayers.has(type));

  const showAttribution = useAttributionInfo();

  return (
    <Toolbar className="me-2 mb-2">
      <Dropdown>
        <LongPressTooltip label={m?.general.infoAndLegal ?? '…'}>
          {({ props }) => (
            <Dropdown.Toggle
              bsPrefix="fm-dropdown-toggle-nocaret"
              id="dropdown-basic"
              variant="secondary"
              {...props}
            >
              <FaInfoCircle />
            </Dropdown.Toggle>
          )}
        </LongPressTooltip>

        <FmDropdownMenu style={{ width: 'max-content' }}>
          <Dropdown.Item
            key="attribution"
            as="button"
            onClick={() => showAttribution()}
          >
            <FaRegCopyright /> {m?.main.copyright}
          </Dropdown.Item>

          {showLegendButton && (
            <OnlineOnlyItem
              {...modalMenuItemProps('legend')}
              onClick={(e) => {
                e.preventDefault();

                dispatch(setActiveModal({ type: 'legend' }));
              }}
            >
              <FaList /> {m?.mainMenu.mapLegend} <kbd>m</kbd> <kbd>l</kbd>
            </OnlineOnlyItem>
          )}

          <Dropdown.Item
            key="privacyPolicy"
            href="#document=privacyPolicy"
            onClick={(e) => {
              e.preventDefault();

              dispatch(documentShow('privacyPolicy'));
            }}
          >
            <FaLock /> {m?.general.privacyPolicy}
          </Dropdown.Item>

          <Dropdown.Item
            key="termsOfService"
            href="#document=termsOfService"
            onClick={(e) => {
              e.preventDefault();

              dispatch(documentShow('termsOfService'));
            }}
          >
            <FaFileContract /> {m?.general.termsOfService}
          </Dropdown.Item>

          <Dropdown.Item
            key="refundPolicy"
            href="#document=refundPolicy"
            onClick={(e) => {
              e.preventDefault();

              dispatch(documentShow('refundPolicy'));
            }}
          >
            <FaMoneyBillWave /> {m?.general.refundPolicy}
          </Dropdown.Item>
        </FmDropdownMenu>
      </Dropdown>
    </Toolbar>
  );
}
